import type { InventoryProfileType } from '@/types/inventory'

export type MasterDataColumnKind = 'identity' | 'profile' | 'status' | 'actions'

export interface MasterDataColumn {
  key: string
  label: string
  kind: MasterDataColumnKind
  /** Set only for `kind: 'profile'` — the profile-type this column opens a popup for. */
  profileType?: InventoryProfileType
}

/**
 * The Master Data table columns in display order (design spec §2.1): 3 identity
 * columns, then the 12 configurable profile columns, then Status + Actions.
 * Profile columns carry the `profileType` their popup edits. Pure data.
 */
export const MASTER_DATA_COLUMNS: MasterDataColumn[] = [
  { key: 'materialId', label: 'Material ID', kind: 'identity' },
  { key: 'materialCode', label: 'Material Code', kind: 'identity' },
  { key: 'materialName', label: 'Material Name', kind: 'identity' },
  { key: 'material_description', label: 'Material Description', kind: 'profile', profileType: 'material_description' },
  { key: 'manufacturer', label: 'Manufacturer', kind: 'profile', profileType: 'manufacturer' },
  { key: 'inventory_unit', label: 'Inventory Unit', kind: 'profile', profileType: 'inventory_unit' },
  { key: 'storage', label: 'Storage Profile', kind: 'profile', profileType: 'storage' },
  // Product Code is an identity column now, not a profile — it lives on the
  // material and is captured when the material is created.
  { key: 'primaryProductCode', label: 'Product Code', kind: 'identity' },
  { key: 'traceability', label: 'Traceability', kind: 'profile', profileType: 'traceability' },
  { key: 'shelf_life', label: 'Shelf Life Days', kind: 'profile', profileType: 'shelf_life' },
  { key: 'quality', label: 'Quality Status', kind: 'profile', profileType: 'quality' },
  { key: 'usage', label: 'Usage', kind: 'profile', profileType: 'usage' },
  { key: 'regulatory', label: 'Regulatory', kind: 'profile', profileType: 'regulatory' },
  { key: 'cost', label: 'Cost', kind: 'profile', profileType: 'cost' },
  { key: 'attachments', label: 'Attachments', kind: 'profile', profileType: 'attachments' },
  { key: 'status', label: 'Status', kind: 'status' },
  { key: 'actions', label: 'Actions', kind: 'actions' },
]

export function isProfileColumn(col: MasterDataColumn): boolean {
  return col.kind === 'profile'
}
